import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import type {
  SKUNeedReviewType,
  GoodValueOverviewType,
  ChartDataType,
  ForecastTableDataType,
  HubDistributionResponseType,
  PaginationType,
} from '../../@types/dpTypes'
import {
  fetchSkuReviewSummary,
  fetchGoodValueOverview,
  fetchTrendOverview,
  fetchForecastProductList,
  fetchHubDistribution,
  downloadForecastCsv,
} from './demandPlanningThunk'

const ERROR_MSG = 'An error occurred'

export interface DemandPlanningStateType {
  isLoading: boolean
  isLoadingDetail: boolean
  error: string | null
  skuNeedReview: Nullable<SKUNeedReviewType>
  goodValueOverview: Nullable<GoodValueOverviewType>
  chartData: ChartDataType[]
  forecastProductList: ForecastTableDataType[]
  forecastPagination: Nullable<PaginationType>
  hubDistribution: Nullable<HubDistributionResponseType['data']>
  hubDistributionPagination: Nullable<PaginationType>
}

const initialState: DemandPlanningStateType = {
  isLoading: false,
  isLoadingDetail: false,
  error: null,
  skuNeedReview: null,
  goodValueOverview: null,
  chartData: [],
  forecastProductList: [],
  forecastPagination: null,
  hubDistribution: null,
  hubDistributionPagination: null,
}

export const sliceName = 'demandPlanning'

const demandPlanningSlice = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    reset: () => initialState,
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setSKUNeedReview: (state, action: PayloadAction<Nullable<SKUNeedReviewType>>) => {
      state.skuNeedReview = action.payload
    },
    setGoodValueOverview: (state, action: PayloadAction<Nullable<GoodValueOverviewType>>) => {
      state.goodValueOverview = action.payload
    },
    setChartData: (state, action: PayloadAction<ChartDataType[]>) => {
      state.chartData = action.payload
    },
    clearHubDistribution: (state) => {
      state.hubDistribution = null
      state.hubDistributionPagination = null
    },
  },
  extraReducers: (builder) => {
    builder
      // SkuReviewSummary
      .addCase(fetchSkuReviewSummary.pending, (state) => {
        state.error = null
      })
      .addCase(fetchSkuReviewSummary.fulfilled, (state, action) => {
        state.skuNeedReview = action.payload?.data ?? null
        state.error = null
      })
      .addCase(fetchSkuReviewSummary.rejected, (state, action) => {
        state.error = action.error.message || ERROR_MSG
      })
      // GoodValueOverview
      .addCase(fetchGoodValueOverview.pending, (state) => {
        state.error = null
      })
      .addCase(fetchGoodValueOverview.fulfilled, (state, action) => {
        state.goodValueOverview = action.payload?.data ?? null
        state.error = null
      })
      .addCase(fetchGoodValueOverview.rejected, (state, action) => {
        state.error = action.error.message || ERROR_MSG
      })
      // TrendOverview
      .addCase(fetchTrendOverview.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchTrendOverview.fulfilled, (state, action) => {
        state.isLoading = false
        state.chartData = action.payload?.data ?? []
        state.error = null
      })
      .addCase(fetchTrendOverview.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || ERROR_MSG
      })
      // ForecastProductList
      .addCase(fetchForecastProductList.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchForecastProductList.fulfilled, (state, action) => {
        state.isLoading = false
        state.forecastProductList = action.payload?.data ?? []
        state.forecastPagination = action.payload?.pagination ?? null
        state.error = null
      })
      .addCase(fetchForecastProductList.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || ERROR_MSG
      })
      // HubDistribution
      .addCase(fetchHubDistribution.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchHubDistribution.fulfilled, (state, action) => {
        state.isLoading = false
        const incoming = action.payload?.data ?? null
        if (action.meta.arg.append && state.hubDistribution && incoming) {
          state.hubDistribution = {
            ...incoming,
            hubs: [...state.hubDistribution.hubs, ...incoming.hubs],
          }
        } else {
          state.hubDistribution = {
            ...incoming,
            product_id: action.meta.arg.productId,
          }
        }
        state.hubDistributionPagination = action.payload?.pagination ?? null
        state.error = null
      })
      .addCase(fetchHubDistribution.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || ERROR_MSG
      })
      // DownloadForecastCsv
      .addCase(downloadForecastCsv.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(downloadForecastCsv.fulfilled, (state) => {
        state.isLoading = false
        state.error = null
      })
      .addCase(downloadForecastCsv.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || ERROR_MSG
      })
  },
})

export const {
  setLoading,
  reset,
  setSKUNeedReview,
  setGoodValueOverview,
  setChartData,
  clearHubDistribution,
} = demandPlanningSlice.actions

export default demandPlanningSlice.reducer
